import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Settings, 
  Tag,
  Route,
  Layers,
  Type,
  Ruler,
  Eye,
  EyeOff,
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Gauge,
  Compass,
  Box,
  Map,
  Home,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

// Default settings
const DEFAULT_SETTINGS = {
  // Mattertag settings
  mattertags: {
    enabled: true,
    showLabels: true,
    showDescriptions: true,
  },
  // Tour settings
  tour: {
    enabled: true,
    autoPlay: false,
    speed: 0.3, // seconds per transition (0.1 to 3)
    transitionStyle: 'fly', // fly, fade, instant
  },
  // Floor settings
  floor: {
    enabled: true,
    showIndicator: true,
  },
  // Label settings
  labels: {
    enabled: true,
    showCustomLabels: true,
  },
  // Measurements settings
  measurements: {
    enabled: true,
    showDimensions: false,
  },
  // Guided Tour settings
  guidedTour: {
    enabled: true,
    stops: ['outside', 'inside', 'outside'],
  },
  // View settings
  view: {
    showMinimap: false,
    showCompass: true,
    defaultMode: 'inside', // inside, dollhouse, floorplan
  },
  // Overlay buttons
  overlayButtons: {
    enabled: true,
    showLabels: true,
  },
  // Native Matterport UI
  nativeUI: {
    showControls: false,   // Bottom controls (zoom, play, etc.)
    showBranding: false,   // Matterport logo
    showHelp: false,       // Help button
    showShare: false,      // Share button
    showVR: false,         // VR button
    showFullscreen: false, // Fullscreen button
  },
};

export function SettingsPanel({ 
  settings = DEFAULT_SETTINGS,
  onSettingsChange,
  sdkInstance,
  isSDKConnected = false,
}) {
  const { canEdit, isViewer, isAdmin, user } = useAuth();
  
  // Create user-specific storage key
  const storageKey = user?.id ? `matterport-settings-${user.id}` : 'matterport-settings';
  
  const [localSettings, setLocalSettings] = useState(() => {
    // First try user-specific key, then fallback to global key for migration
    let saved = localStorage.getItem(storageKey);
    if (!saved && user?.id) {
      // Check if there's a global setting to migrate
      saved = localStorage.getItem('matterport-settings');
    }
    if (saved) {
      try {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
      } catch (e) {
        console.warn('Failed to parse saved settings:', e);
      }
    }
    return DEFAULT_SETTINGS;
  });

  const isInitialMountRef = useRef(true);

  // Save settings to localStorage and notify parent when they change
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(localSettings));
    
    // Only call onSettingsChange after initial mount to avoid resetting parent state
    if (!isInitialMountRef.current && onSettingsChange) {
      onSettingsChange(localSettings);
    }
    isInitialMountRef.current = false;
  }, [localSettings, onSettingsChange, storageKey]);

  const updateSetting = (category, key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
  };

  const resetSettings = () => {
    setLocalSettings(DEFAULT_SETTINGS);
  };

  // Feature availability based on SDK connection
  const featureStatus = {
    mattertags: isSDKConnected,
    tour: isSDKConnected,
    floor: isSDKConnected,
    labels: isSDKConnected,
    measurements: isSDKConnected,
    guidedTour: isSDKConnected,
    view: true, // Always available
    overlayButtons: true, // Always available
  };

  // Hide settings panel for viewers
  if (isViewer) {
    return null;
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="glass" size="icon" className="rounded-xl">
          <Settings className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Settings & Features
          </SheetTitle>
          <SheetDescription>
            Configure what is visible and enabled in the 3D viewer.
            {!isSDKConnected && (
              <span className="block mt-1 text-yellow-500">
                ⚠️ Some features require SDK connection
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Quick Status */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                isSDKConnected ? "bg-green-500" : "bg-yellow-500"
              )} />
              <span className="text-sm font-medium">
                SDK {isSDKConnected ? 'Connected' : 'Limited'}
              </span>
            </div>
          </div>

          <Accordion type="multiple" defaultValue={['mattertags', 'tour', 'view']} className="w-full">
            
            {/* Mattertags Settings */}
            <AccordionItem value="mattertags">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  <span>Mattertags</span>
                  <Badge variant={localSettings.mattertags.enabled ? 'default' : 'secondary'} className="ml-2 text-[10px]">
                    {localSettings.mattertags.enabled ? 'ON' : 'OFF'}
                  </Badge>
                  {!featureStatus.mattertags && (
                    <Badge variant="outline" className="text-[10px] text-yellow-500">SDK Required</Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Enable Mattertags</Label>
                    <p className="text-xs text-muted-foreground">Show tag markers in 3D space</p>
                  </div>
                  <Switch
                    checked={localSettings.mattertags.enabled}
                    onCheckedChange={(checked) => updateSetting('mattertags', 'enabled', checked)}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Labels</Label>
                    <p className="text-xs text-muted-foreground">Display tag names</p>
                  </div>
                  <Switch
                    checked={localSettings.mattertags.showLabels}
                    onCheckedChange={(checked) => updateSetting('mattertags', 'showLabels', checked)}
                    disabled={!localSettings.mattertags.enabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Descriptions</Label>
                    <p className="text-xs text-muted-foreground">Show details on hover/click</p>
                  </div>
                  <Switch
                    checked={localSettings.mattertags.showDescriptions}
                    onCheckedChange={(checked) => updateSetting('mattertags', 'showDescriptions', checked)}
                    disabled={!localSettings.mattertags.enabled}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Tour Settings */}
            <AccordionItem value="tour">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Route className="h-4 w-4 text-primary" />
                  <span>Tours & Guided Tour</span>
                  <Badge variant={localSettings.tour.enabled ? 'default' : 'secondary'} className="ml-2 text-[10px]">
                    {localSettings.tour.enabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Enable Tours</Label>
                    <p className="text-xs text-muted-foreground">Allow tour playback</p>
                  </div>
                  <Switch
                    checked={localSettings.tour.enabled}
                    onCheckedChange={(checked) => updateSetting('tour', 'enabled', checked)}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Auto-Play Tours</Label>
                    <p className="text-xs text-muted-foreground">Start tour automatically on load</p>
                  </div>
                  <Switch
                    checked={localSettings.tour.autoPlay}
                    onCheckedChange={(checked) => updateSetting('tour', 'autoPlay', checked)}
                    disabled={!localSettings.tour.enabled}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Transition Speed</Label>
                    <span className="text-xs text-muted-foreground">{localSettings.tour.speed}s</span>
                  </div>
                  <Slider
                    value={[localSettings.tour.speed]}
                    onValueChange={([value]) => updateSetting('tour', 'speed', value)}
                    min={0.1}
                    max={3}
                    step={0.1}
                    disabled={!localSettings.tour.enabled}
                  />
                  <p className="text-xs text-muted-foreground">0.1s (fastest) to 3s (slowest)</p>
                </div>
                {/* Transition Style - Admin Only */}
                {isAdmin && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Transition Style</Label>
                        <p className="text-xs text-muted-foreground">Animation when moving between scans</p>
                      </div>
                      <Select
                        value={localSettings.tour.transitionStyle || 'fly'}
                        onValueChange={(value) => updateSetting('tour', 'transitionStyle', value)}
                        disabled={!localSettings.tour.enabled}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fly">Fly</SelectItem>
                          <SelectItem value="fade">Fade</SelectItem>
                          <SelectItem value="instant">Instant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Guided Tour</Label>
                    <p className="text-xs text-muted-foreground">Outside → Inside → Outside path</p>
                  </div>
                  <Switch
                    checked={localSettings.guidedTour.enabled}
                    onCheckedChange={(checked) => updateSetting('guidedTour', 'enabled', checked)}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Floor Settings */}
            <AccordionItem value="floor">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <span>Floors</span>
                  <Badge variant={localSettings.floor.enabled ? 'default' : 'secondary'} className="ml-2 text-[10px]">
                    {localSettings.floor.enabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Enable Floor Switching</Label>
                    <p className="text-xs text-muted-foreground">Navigate between floors</p>
                  </div>
                  <Switch
                    checked={localSettings.floor.enabled}
                    onCheckedChange={(checked) => updateSetting('floor', 'enabled', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Floor Indicator</Label>
                    <p className="text-xs text-muted-foreground">Display current floor</p>
                  </div>
                  <Switch
                    checked={localSettings.floor.showIndicator}
                    onCheckedChange={(checked) => updateSetting('floor', 'showIndicator', checked)}
                    disabled={!localSettings.floor.enabled}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Labels Settings */}
            <AccordionItem value="labels">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Type className="h-4 w-4 text-primary" />
                  <span>Labels</span>
                  <Badge variant={localSettings.labels.enabled ? 'default' : 'secondary'} className="ml-2 text-[10px]">
                    {localSettings.labels.enabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Enable Labels</Label>
                    <p className="text-xs text-muted-foreground">Show text labels in 3D space</p>
                  </div>
                  <Switch
                    checked={localSettings.labels.enabled}
                    onCheckedChange={(checked) => updateSetting('labels', 'enabled', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Custom Labels</Label>
                    <p className="text-xs text-muted-foreground">Display your created labels</p>
                  </div>
                  <Switch
                    checked={localSettings.labels.showCustomLabels}
                    onCheckedChange={(checked) => updateSetting('labels', 'showCustomLabels', checked)}
                    disabled={!localSettings.labels.enabled}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Measurements Settings */}
            <AccordionItem value="measurements">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Ruler className="h-4 w-4 text-primary" />
                  <span>Measurements</span>
                  <Badge variant={localSettings.measurements.enabled ? 'default' : 'secondary'} className="ml-2 text-[10px]">
                    {localSettings.measurements.enabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Enable Measurements</Label>
                    <p className="text-xs text-muted-foreground">Show distance measurements</p>
                  </div>
                  <Switch
                    checked={localSettings.measurements.enabled}
                    onCheckedChange={(checked) => updateSetting('measurements', 'enabled', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Dimensions</Label>
                    <p className="text-xs text-muted-foreground">Display dimension annotations</p>
                  </div>
                  <Switch
                    checked={localSettings.measurements.showDimensions}
                    onCheckedChange={(checked) => updateSetting('measurements', 'showDimensions', checked)}
                    disabled={!localSettings.measurements.enabled}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* View Settings */}
            <AccordionItem value="view">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  <span>View Options</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Compass</Label>
                    <p className="text-xs text-muted-foreground">Display orientation compass</p>
                  </div>
                  <Switch
                    checked={localSettings.view.showCompass}
                    onCheckedChange={(checked) => updateSetting('view', 'showCompass', checked)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Default View Mode</Label>
                  <div className="flex gap-2">
                    {[
                      { key: 'inside', icon: Home, label: 'Inside' },
                      { key: 'dollhouse', icon: Box, label: 'Dollhouse' },
                      { key: 'floorplan', icon: Map, label: 'Floor Plan' },
                    ].map(({ key, icon: Icon, label }) => (
                      <Button
                        key={key}
                        variant={localSettings.view.defaultMode === key ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateSetting('view', 'defaultMode', key)}
                        className="flex-1"
                      >
                        <Icon className="h-3 w-3 mr-1" />
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Overlay Buttons Settings */}
            <AccordionItem value="overlayButtons">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Compass className="h-4 w-4 text-primary" />
                  <span>Overlay Buttons</span>
                  <Badge variant={localSettings.overlayButtons.enabled ? 'default' : 'secondary'} className="ml-2 text-[10px]">
                    {localSettings.overlayButtons.enabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Enable Overlay Buttons</Label>
                    <p className="text-xs text-muted-foreground">Show navigation buttons on viewer</p>
                  </div>
                  <Switch
                    checked={localSettings.overlayButtons.enabled}
                    onCheckedChange={(checked) => updateSetting('overlayButtons', 'enabled', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Button Labels</Label>
                    <p className="text-xs text-muted-foreground">Display text on buttons</p>
                  </div>
                  <Switch
                    checked={localSettings.overlayButtons.showLabels}
                    onCheckedChange={(checked) => updateSetting('overlayButtons', 'showLabels', checked)}
                    disabled={!localSettings.overlayButtons.enabled}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Native Matterport UI Settings */}
            <AccordionItem value="nativeUI">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <EyeOff className="h-4 w-4 text-primary" />
                  <span>Matterport Native UI</span>
                  <Badge variant={localSettings.nativeUI?.showControls ? 'default' : 'secondary'} className="ml-2 text-[10px]">
                    {localSettings.nativeUI?.showControls ? 'VISIBLE' : 'HIDDEN'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <p className="text-xs text-muted-foreground mb-2">
                  Control visibility of Matterport default UI elements. Changes require page reload.
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Controls</Label>
                    <p className="text-xs text-muted-foreground">Bottom bar with zoom, play, etc.</p>
                  </div>
                  <Switch
                    checked={localSettings.nativeUI?.showControls ?? false}
                    onCheckedChange={(checked) => updateSetting('nativeUI', 'showControls', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Branding</Label>
                    <p className="text-xs text-muted-foreground">Matterport logo</p>
                  </div>
                  <Switch
                    checked={localSettings.nativeUI?.showBranding ?? false}
                    onCheckedChange={(checked) => updateSetting('nativeUI', 'showBranding', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Help</Label>
                    <p className="text-xs text-muted-foreground">Help and terms links</p>
                  </div>
                  <Switch
                    checked={localSettings.nativeUI?.showHelp ?? false}
                    onCheckedChange={(checked) => updateSetting('nativeUI', 'showHelp', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Share</Label>
                    <p className="text-xs text-muted-foreground">Share button</p>
                  </div>
                  <Switch
                    checked={localSettings.nativeUI?.showShare ?? false}
                    onCheckedChange={(checked) => updateSetting('nativeUI', 'showShare', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show VR</Label>
                    <p className="text-xs text-muted-foreground">VR mode button</p>
                  </div>
                  <Switch
                    checked={localSettings.nativeUI?.showVR ?? false}
                    onCheckedChange={(checked) => updateSetting('nativeUI', 'showVR', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show Fullscreen</Label>
                    <p className="text-xs text-muted-foreground">Fullscreen button</p>
                  </div>
                  <Switch
                    checked={localSettings.nativeUI?.showFullscreen ?? false}
                    onCheckedChange={(checked) => updateSetting('nativeUI', 'showFullscreen', checked)}
                  />
                </div>
                <Separator />
                <p className="text-xs text-yellow-500">
                  💡 Reload the page after changing these settings for them to take effect.
                </p>
              </AccordionContent>
            </AccordionItem>

          </Accordion>

          {/* Feature Info */}
          <div className="p-3 rounded-lg bg-muted/50 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Info className="h-4 w-4" />
              Available SDK Features
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                { name: 'Mattertags', available: featureStatus.mattertags },
                { name: 'Tours', available: featureStatus.tour },
                { name: 'Floor Nav', available: featureStatus.floor },
                { name: 'Labels', available: featureStatus.labels },
                { name: 'Measurements', available: featureStatus.measurements },
                { name: 'Guided Tour', available: featureStatus.guidedTour },
              ].map(({ name, available }) => (
                <div key={name} className="flex items-center gap-1">
                  {available ? (
                    <Eye className="h-3 w-3 text-green-500" />
                  ) : (
                    <EyeOff className="h-3 w-3 text-yellow-500" />
                  )}
                  <span className={cn(available ? 'text-foreground' : 'text-muted-foreground')}>
                    {name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Reset Button - At the bottom */}
          <div className="pt-4 border-t">
            <Button variant="outline" size="sm" onClick={resetSettings} className="w-full">
              <RotateCcw className="h-3 w-3 mr-2" />
              Reset All Settings
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default SettingsPanel;
